import { fx_crearBaseLocal, reqToPromise, txDone } from "../../../../utils/idbUtils";

const DB_NAME = "grpc_monitoreo_sesion";
const DB_VERSION = 5;
const STORE_SESIONES = "sesiones";
const STORE_LOTES = "lotes";
const STORE_SYNC = "sync";

/** Item mínimo que necesita el merge de delta (mismo contrato que
 *  `GrpcAplicacionItem`/`GrpcLoteItem`: watermark por finca, no uno global). */
export type GrpcMonitoreoSesionItem = {
  id: string;
  eliminado: boolean;
  ultimaActualizacion: bigint;
  fincaId: string;
};

/** Catálogo de un lote referenciado por alguna sesión (id + nombre). Se
 *  acumula (upsert), nunca se reemplaza entero: un delta solo trae los lotes
 *  de las sesiones que cambiaron, no los de todo el histórico (ver
 *  `SesionDashboardDeltaResponse` del Server). */
export type GrpcMonitoreoSesionLoteItem = {
  id: string;
  nombre: string;
};

/** IndexedDB **nueva y separada** para el dashboard histórico de sesiones de
 *  monitoreo que trae `SesionDashboardGrpcService` (ver CLAUDE.md del Server,
 *  sección migración a gRPC-Web, 2026-09-15) — mismo patrón que
 *  `GrpcAplicacionDatabase`/`GrpcLoteDatabase`: delta real por `fincaId`.
 *
 *  El año ya no lo filtra el server (antes `GetDashboardByFincaId` aceptaba
 *  `anio`) — se sincroniza TODO el histórico de la finca con delta, y el año
 *  se filtra en memoria sobre lo que ya está en este store (mismo criterio
 *  que `agruparPorSemana.ts`/las "YearGroupList" de otros dominios). */
class GrpcMonitoreoSesionDatabase {
  /** Conexión a la base local con el ayudante común, que al cambiar de
   *  versión conserva los datos (ver `fx_crearBaseLocal`). La versión 3
   *  (2026-09-30) se subió a pedido del usuario para una limpieza única al
   *  estrenar el ayudante: un navegador con una versión anterior vacía la
   *  base una sola vez y la vuelve a llenar desde el servidor.
   *
   *  **Versión 4 (2026-10-16): cambió la forma del item.** El dashboard ahora
   *  trae `afectados` y `muestra` por lote, y acá la sesión se guarda tal como
   *  llega. Las sesiones cacheadas antes no los tienen, el sync por delta no las
   *  vuelve a pedir -no cambiaron en el servidor- y leer `afectados` de un item
   *  que no lo trae revienta el listado entero. Vaciar y rellenar es la única
   *  forma de que los registros viejos traigan los campos nuevos.
   *
   *  **Versión 5 (2026-10-20): volvió a cambiar la forma del item.** El dashboard
   *  ahora trae también `referencias` por lote y organismo —con qué valores se
   *  evaluó cada uno, congelados en la sesión—, y el motor de evaluación las lee
   *  en vez de los umbrales vigentes del catálogo. Mismo motivo que la 4: el
   *  delta no puede anunciar un campo que antes no existía. */
  private readonly _base = fx_crearBaseLocal(
    DB_NAME,
    DB_VERSION,
    [
      { nombre: STORE_SESIONES, clave: "id", indices: ["fincaId"] },
      { nombre: STORE_LOTES, clave: "id" },
      { nombre: STORE_SYNC, clave: "fincaId" },
    ],
    { vaciarAntesDe: 5 },
  );

  /** Conexión a la base local, abierta la primera vez y reusada después. */
  private open(): Promise<IDBDatabase> {
    return this._base.abrir();
  }

  /** Watermark de la última sincronización exitosa de una finca. `0n` si
   *  todavía no sincronizó nunca (trae el histórico completo). */
  async getUltimaSincronizacionAsync(fincaId: string): Promise<bigint> {
    const db = await this.open();
    const store = db.transaction(STORE_SYNC, "readonly").objectStore(STORE_SYNC);
    const row = await reqToPromise<{ fincaId: string; ultimaSincronizacion: string } | undefined>(
      store.get(fincaId),
    );
    return row ? BigInt(row.ultimaSincronizacion) : 0n;
  }

  /** Aplica un delta de una finca: `put` de las sesiones vivas, `delete` de
   *  las marcadas `eliminado`, `put` (upsert) de los lotes referenciados, y
   *  guarda el nuevo watermark de esa finca. */
  async putDeltaAsync<T extends GrpcMonitoreoSesionItem>(
    fincaId: string,
    sesiones: readonly T[],
    lotes: readonly GrpcMonitoreoSesionLoteItem[],
    nuevoWatermark: bigint,
  ): Promise<void> {
    const db = await this.open();
    const tx = db.transaction([STORE_SESIONES, STORE_LOTES, STORE_SYNC], "readwrite");
    const sesionesStore = tx.objectStore(STORE_SESIONES);
    const lotesStore = tx.objectStore(STORE_LOTES);
    const syncStore = tx.objectStore(STORE_SYNC);

    for (const sesion of sesiones) {
      if (sesion.eliminado) {
        sesionesStore.delete(sesion.id);
      } else {
        sesionesStore.put(sesion);
      }
    }
    for (const lote of lotes) {
      lotesStore.put(lote);
    }
    syncStore.put({ fincaId, ultimaSincronizacion: nuevoWatermark.toString() });

    await txDone(tx);
  }

  /** Todas las sesiones guardadas de una finca, vía el índice `fincaId`. */
  async getSesionesByFincaIdAsync<T>(fincaId: string): Promise<T[]> {
    const db = await this.open();
    const index = db
      .transaction(STORE_SESIONES, "readonly")
      .objectStore(STORE_SESIONES)
      .index("fincaId");
    return reqToPromise<T[]>(index.getAll(fincaId));
  }

  /** Nombre de los lotes pedidos por id (`undefined` si ese lote todavía no
   *  se vio en ningún delta). */
  async getLotesByIdsAsync(ids: readonly string[]): Promise<GrpcMonitoreoSesionLoteItem[]> {
    const db = await this.open();
    const store = db.transaction(STORE_LOTES, "readonly").objectStore(STORE_LOTES);
    const lotes = await Promise.all(
      ids.map((id) => reqToPromise<GrpcMonitoreoSesionLoteItem | undefined>(store.get(id))),
    );
    return lotes.filter((lote): lote is GrpcMonitoreoSesionLoteItem => lote !== undefined);
  }
}

/** Singleton del dashboard de sesiones de monitoreo por gRPC-Web (con delta por finca). */
export const grpcMonitoreoSesionDatabase = new GrpcMonitoreoSesionDatabase();
